import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ComposeEmailModal } from './ComposeEmailModal';
import { AddTaskModal } from './ContactDetailPage';

// ============================================================
// Types
// ============================================================

interface Company {
  id: number;
  name: string;
  domain: string | null;
  website: string | null;
  industry: string | null;
  notes: string | null;
  customer_user_id: number | null;
  customer_user_email: string | null;
  customer_user_first_name: string | null;
  customer_user_last_name: string | null;
  customer_user_created_at: string | null;
  subscription_status: string | null;
  stripe_customer_id: string | null;
  stripe_invoices_customer_id: string | null;
}

interface Contact {
  id: number;
  full_name: string;
  email: string | null;
  job_title: string | null;
  priority: number;
  stage: string;
  last_demo_at: string | null;
}

interface Deal {
  id: number;
  name: string;
  stage: string;
  value_pence: number | null;
  won_at: string | null;
  lost_at: string | null;
  primary_contact_name: string | null;
  primary_contact_email: string | null;
  created_at: string;
}

interface Demo {
  id: number;
  scheduled_at: string;
  duration_mins: number | null;
  outcome: string;
  title: string | null;
  contact_name: string;
}

interface Invoice {
  id: number;
  account_label: 'primary' | 'invoices' | 'manual' | string;
  source_type?: string;
  stripe_invoice_id: string;
  customer_email: string | null;
  customer_name: string | null;
  amount_paid_pence: number;
  amount_due_pence: number;
  currency: string;
  status: string;
  description: string | null;
  hosted_invoice_url: string | null;
  invoice_number: string | null;
  paid_at: string | null;
  invoice_created_at: string;
}

interface InvoiceTotalsBucket { pence: number; count: number }
interface InvoiceTotals {
  total?: InvoiceTotalsBucket;
  primary?: InvoiceTotalsBucket;
  invoices?: InvoiceTotalsBucket;
}

interface CompanyDetail {
  company: Company;
  contacts: Contact[];
  deals: Deal[];
  demos: Demo[];
  invoices: Invoice[];
  invoiceTotals: InvoiceTotals;
}

interface Email {
  id: number;
  external_id: string;
  thread_id: string | null;
  subject: string | null;
  body_preview: string | null;
  from_email: string | null;
  from_name: string | null;
  to_emails: Array<{ email: string; name: string | null }>;
  direction: 'inbound' | 'outbound';
  sent_at: string;
  linked_contact_id: number | null;
}

// ============================================================
// Helpers
// ============================================================

const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};
const formatDaysAgo = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
};
const formatPence = (p: number | null | undefined): string => {
  if (p == null) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(p / 100);
};

// ============================================================
// Main page
// ============================================================

export const CompanyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<CompanyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [emails, setEmails] = useState<Email[] | null>(null);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState<Email | null>(null);
  const [composeNew, setComposeNew] = useState<{ to: string; toName?: string } | null>(null);
  const [tasks, setTasks] = useState<any[] | null>(null);
  const [showAddTask, setShowAddTask] = useState(false);

  const loadTasks = async () => {
    if (!id) return;
    const res = await fetch(`/api/crm/tasks?companyId=${id}&includeCompleted=1`, { headers: authHeaders() });
    if (res.ok) {
      const data = await res.json();
      setTasks(data.tasks || []);
    }
  };

  useEffect(() => { loadTasks(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/crm/companies/${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load company');
      setData(await res.json());
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  // Load emails separately so the page renders fast and emails fill
  // in once they're back (could be slow if there are many).
  useEffect(() => {
    if (!id) return;
    setEmailsLoading(true);
    fetch(`/api/crm/emails?companyId=${id}&limit=50`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : { emails: [] })
      .then(d => setEmails(d.emails || []))
      .finally(() => setEmailsLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading company…</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => navigate('/admin/crm')} className="text-blue-600 hover:underline text-sm mb-4">← Back to CRM</button>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-800">
            Failed to load company. {error}
          </div>
        </div>
      </div>
    );
  }

  const c = data.company;
  const lifetimeValue = data.invoiceTotals.total?.pence || 0;
  const wonDealsValue = data.deals
    .filter(d => d.won_at)
    .reduce((acc, d) => acc + (d.value_pence || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header / breadcrumb */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <button onClick={() => navigate('/admin/crm')} className="text-xs text-blue-600 hover:underline">← All companies</button>
            <h1 className="text-3xl font-bold text-gray-900 mt-1">{c.name}</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {c.domain}
              {c.industry && ` · ${c.industry}`}
              {c.website && <> · <a className="text-blue-600 hover:underline" href={c.website} target="_blank" rel="noreferrer">{c.website}</a></>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {c.customer_user_id && (
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">
                Paying customer
              </span>
            )}
            <button onClick={load} className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">
              Refresh
            </button>
          </div>
        </div>

        {/* Lifetime value summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <BigStat
            label="Lifetime value"
            value={formatPence(lifetimeValue)}
            sub={`${data.invoiceTotals.total?.count || 0} invoices · ${formatPence(wonDealsValue)} in won deals`}
            tone="green"
          />
          <BigStat
            label="Contacts"
            value={String(data.contacts.length)}
            sub={`${data.deals.length} deals total`}
          />
          <BigStat
            label="Demos"
            value={String(data.demos.length)}
            sub={data.demos[0] ? `last ${formatDaysAgo(data.demos[0].scheduled_at)}` : 'none yet'}
          />
          <BigStat
            label="Status"
            value={
              c.customer_user_id ? 'Active customer'
              : data.deals.some(d => d.won_at) ? 'Past customer'
              : data.deals.some(d => !d.won_at && !d.lost_at) ? 'In pipeline'
              : data.deals.some(d => d.lost_at) ? 'Lost'
              : 'Prospect'
            }
            sub={c.subscription_status || ''}
            tone={c.customer_user_id ? 'green' : 'gray'}
          />
        </div>

        {/* Stripe / SaaS linkage box */}
        {(c.customer_user_id || c.stripe_customer_id) && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm">
            <div className="font-semibold text-green-900 mb-1">Customer record</div>
            {c.customer_user_email && (
              <div className="text-green-800">
                {c.customer_user_first_name} {c.customer_user_last_name} &lt;{c.customer_user_email}&gt;
                {c.customer_user_created_at && <span className="text-green-700 ml-2">· since {formatDate(c.customer_user_created_at)}</span>}
              </div>
            )}
            <div className="text-xs text-green-700 mt-1 space-y-0.5">
              {c.stripe_customer_id && (
                <div>Stripe (primary): <code className="bg-green-100 px-1 rounded">{c.stripe_customer_id}</code></div>
              )}
              {c.stripe_invoices_customer_id && (
                <div>Stripe (invoices): <code className="bg-green-100 px-1 rounded">{c.stripe_invoices_customer_id}</code></div>
              )}
            </div>
          </div>
        )}

        {/* Invoices */}
        <Section title={`Invoices & payments (${data.invoices.length})`}
          right={
            <div className="flex items-center gap-3">
              <div className="text-xs text-gray-600">
                {data.invoiceTotals.primary && (
                  <span className="mr-3">
                    <span className="text-gray-500">Subscriptions:</span>{' '}
                    <span className="font-semibold text-gray-900">{formatPence(data.invoiceTotals.primary.pence)}</span>{' '}
                    ({data.invoiceTotals.primary.count})
                  </span>
                )}
                {data.invoiceTotals.invoices && (
                  <span className="mr-3">
                    <span className="text-gray-500">Mapping:</span>{' '}
                    <span className="font-semibold text-gray-900">{formatPence(data.invoiceTotals.invoices.pence)}</span>{' '}
                    ({data.invoiceTotals.invoices.count})
                  </span>
                )}
                {(data.invoiceTotals as any).manual && (
                  <span>
                    <span className="text-gray-500">Manual:</span>{' '}
                    <span className="font-semibold text-gray-900">{formatPence((data.invoiceTotals as any).manual.pence)}</span>{' '}
                    ({(data.invoiceTotals as any).manual.count})
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowAddPayment(true)}
                className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                + Add manual payment
              </button>
            </div>
          }
        >
          {data.invoices.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No invoices or payments linked to this company yet. Click "+ Add manual payment" to log one.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-gray-500 border-b">
                <tr>
                  <th className="py-2 pr-3">Invoice / payment</th>
                  <th className="py-2 pr-3">Source</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3 text-right">Amount</th>
                  <th className="py-2 pr-3 text-right">Paid</th>
                  <th className="py-2 pr-3 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.invoices.map(inv => {
                  const isManual = inv.account_label === 'manual' || inv.source_type === 'manual';
                  return (
                    <tr key={inv.id} className="hover:bg-gray-50">
                      <td className="py-2 pr-3">
                        <div className="font-medium text-gray-900">
                          {inv.invoice_number || (isManual ? 'Manual payment' : `#${inv.stripe_invoice_id.slice(-8)}`)}
                        </div>
                        {inv.description && <div className="text-xs text-gray-500 truncate max-w-[400px]">{inv.description}</div>}
                      </td>
                      <td className="py-2 pr-3 text-xs">
                        <span className={`px-1.5 py-0.5 rounded ${
                          inv.account_label === 'manual'   ? 'bg-amber-100 text-amber-800'  :
                          inv.account_label === 'invoices' ? 'bg-purple-100 text-purple-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {inv.account_label === 'manual' ? 'Manual / BACS'
                            : inv.account_label === 'invoices' ? 'Mapping'
                            : 'Subscription'}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-xs">
                        <span className={`px-1.5 py-0.5 rounded ${
                          inv.status === 'paid' ? 'bg-green-100 text-green-800' :
                          inv.status === 'open' ? 'bg-amber-100 text-amber-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-right font-medium text-gray-900">{formatPence(inv.amount_paid_pence || inv.amount_due_pence)}</td>
                      <td className="py-2 pr-3 text-right text-xs text-gray-500">{formatDate(inv.paid_at)}</td>
                      <td className="py-2 pr-3 text-right">
                        {isManual && (
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Delete manual payment of ${formatPence(inv.amount_paid_pence)}?`)) return;
                              await fetch(`/api/crm/invoices/${inv.id}`, { method: 'DELETE', headers: authHeaders() });
                              load();
                            }}
                            className="text-gray-300 hover:text-red-600 text-sm leading-none"
                            title="Delete manual payment"
                          >×</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Section>

        {/* Contacts */}
        {/* Tasks attached to the company itself */}
        <Section
          title={`Tasks (${(tasks || []).filter(t => !t.completed_at).length} open)`}
          right={
            <button onClick={() => setShowAddTask(true)}
              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">+ Task</button>
          }
        >
          {!tasks || tasks.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No company-level tasks yet. Add one for "follow up with {c.name} in general" or "chase the proposal".</p>
          ) : (
            <ul className="space-y-1.5">
              {tasks.filter(t => !t.completed_at).map(t => (
                <CompanyTaskRow key={t.id} task={t} onChanged={loadTasks} />
              ))}
              {tasks.filter(t => t.completed_at).length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                    Completed ({tasks.filter(t => t.completed_at).length})
                  </summary>
                  <ul className="mt-2 space-y-1.5">
                    {tasks.filter(t => t.completed_at).map(t => (
                      <CompanyTaskRow key={t.id} task={t} onChanged={loadTasks} />
                    ))}
                  </ul>
                </details>
              )}
            </ul>
          )}
        </Section>

        <Section title={`Contacts (${data.contacts.length})`}>
          {data.contacts.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No contacts at this company yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {data.contacts.map(c => (
                <li
                  key={c.id}
                  className="py-2.5 flex items-center justify-between text-sm group hover:bg-gray-50 -mx-2 px-2 rounded"
                >
                  <div
                    className="min-w-0 cursor-pointer flex-1"
                    onClick={() => navigate(`/admin/crm/contacts/${c.id}`)}
                  >
                    <div className="font-medium text-blue-700 hover:underline">{c.full_name}</div>
                    <div className="text-xs text-gray-500">
                      {c.job_title && `${c.job_title} · `}
                      {c.email}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 shrink-0">
                    {c.priority > 0 && <span title={`Priority ${c.priority}/5`}>{'🔥'.repeat(c.priority)}</span>}
                    <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px]">{c.stage}</span>
                    {c.last_demo_at && <span>last demo {formatDaysAgo(c.last_demo_at)}</span>}
                    {c.email && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setComposeNew({ to: c.email!, toName: c.full_name }); }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity px-2 py-0.5 bg-blue-600 text-white rounded text-[10px] hover:bg-blue-700"
                      >
                        ✉ Email
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Deals */}
        <Section title={`Deals (${data.deals.length})`}>
          {data.deals.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No deals tracked for this company yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {data.deals.map(d => (
                <li key={d.id} className="py-2.5 flex items-center justify-between text-sm">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900">{d.name}</div>
                    <div className="text-xs text-gray-500">
                      {d.primary_contact_name && `Contact: ${d.primary_contact_name} · `}
                      Created {formatDate(d.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs shrink-0">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      d.won_at ? 'bg-green-100 text-green-800' :
                      d.lost_at ? 'bg-gray-100 text-gray-600' :
                      'bg-blue-100 text-blue-800'
                    }`}>{d.stage}</span>
                    {d.value_pence != null && <span className="font-medium">{formatPence(d.value_pence)}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Emails */}
        <Section title={`Emails${emails != null ? ` (${emails.length})` : ''}`}>
          {emailsLoading ? (
            <p className="text-sm text-gray-400 italic">Loading emails…</p>
          ) : !emails || emails.length === 0 ? (
            <p className="text-sm text-gray-400 italic">
              No emails linked to this company yet. Make sure Outlook Mail.Read is granted in Azure and the email sync has run (every 15 min, or trigger from the CRM page).
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {emails.slice(0, 25).map(e => (
                <li key={e.id} className="py-2 flex items-start gap-3 text-sm group">
                  <span
                    title={e.direction === 'inbound' ? 'Received' : 'Sent'}
                    className={`shrink-0 mt-1 inline-block w-2 h-2 rounded-full ${
                      e.direction === 'inbound' ? 'bg-blue-500' : 'bg-gray-300'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{e.subject || '(no subject)'}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {e.direction === 'inbound'
                        ? <>From <span className="text-gray-700">{e.from_name || e.from_email}</span></>
                        : <>To <span className="text-gray-700">{e.to_emails?.[0]?.name || e.to_emails?.[0]?.email || '?'}</span></>
                      }
                      {' · '}{formatDateTime(e.sent_at)}
                    </div>
                    {e.body_preview && <p className="text-xs text-gray-600 mt-1 line-clamp-2">{e.body_preview}</p>}
                  </div>
                  {e.direction === 'inbound' && e.from_email && (
                    <button
                      onClick={() => setReplyingTo(e)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Reply
                    </button>
                  )}
                </li>
              ))}
              {emails.length > 25 && (
                <li className="py-2 text-xs text-gray-400 italic">+ {emails.length - 25} older emails</li>
              )}
            </ul>
          )}
        </Section>

        {/* Manual payment modal */}
        {showAddPayment && (
          <AddPaymentModal
            companyId={c.id}
            companyName={c.name}
            onClose={() => setShowAddPayment(false)}
            onSaved={() => { setShowAddPayment(false); load(); }}
          />
        )}

        {/* Reply / Compose modals */}
        {replyingTo && (
          <ComposeEmailModal
            replyToEmailId={replyingTo.id}
            to={replyingTo.from_email || undefined}
            toName={replyingTo.from_name || undefined}
            replyOriginalSubject={replyingTo.subject || undefined}
            replyOriginalPreview={replyingTo.body_preview || undefined}
            onClose={() => setReplyingTo(null)}
            onSent={() => {
              setReplyingTo(null);
              // Re-fetch emails after a short delay so the new sent
              // email appears (server-side incremental sync runs in
              // the background after the send).
              setTimeout(() => {
                if (!id) return;
                fetch(`/api/crm/emails?companyId=${id}&limit=50`, { headers: authHeaders() })
                  .then(r => r.ok ? r.json() : { emails: [] })
                  .then(d => setEmails(d.emails || []));
              }, 1500);
            }}
          />
        )}
        {composeNew && (
          <ComposeEmailModal
            to={composeNew.to}
            toName={composeNew.toName}
            onClose={() => setComposeNew(null)}
            onSent={() => {
              setComposeNew(null);
              setTimeout(() => {
                if (!id) return;
                fetch(`/api/crm/emails?companyId=${id}&limit=50`, { headers: authHeaders() })
                  .then(r => r.ok ? r.json() : { emails: [] })
                  .then(d => setEmails(d.emails || []));
              }, 1500);
            }}
          />
        )}
        {showAddTask && (
          <AddTaskModal
            target={{ type: 'company', id: c.id, name: c.name }}
            onClose={() => setShowAddTask(false)}
            onSaved={() => { setShowAddTask(false); loadTasks(); }}
          />
        )}

        {/* Demos */}
        <Section title={`Recent demos (${data.demos.length})`}>
          {data.demos.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No demos yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {data.demos.slice(0, 20).map(d => (
                <li key={d.id} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium text-gray-900">{d.title || 'Untitled demo'}</div>
                    <div className="text-xs text-gray-500">
                      With {d.contact_name} · {formatDateTime(d.scheduled_at)}
                      {d.duration_mins ? ` · ${d.duration_mins}min` : ''}
                    </div>
                  </div>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                    d.outcome === 'completed' ? 'bg-green-100 text-green-800' :
                    d.outcome === 'no_show'   ? 'bg-red-100 text-red-800' :
                    d.outcome === 'cancelled' ? 'bg-gray-100 text-gray-600' :
                    'bg-blue-100 text-blue-800'
                  }`}>{d.outcome}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
};

const BigStat: React.FC<{ label: string; value: string; sub?: string; tone?: 'green' | 'gray' }> = ({ label, value, sub, tone = 'gray' }) => (
  <div className={`bg-white rounded-lg shadow p-5 border-l-4 ${
    tone === 'green' ? 'border-l-green-500' : 'border-l-gray-300'
  }`}>
    <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
    <div className="text-3xl font-bold text-gray-900 mt-1.5">{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
  </div>
);

const CompanyTaskRow: React.FC<{ task: any; onChanged: () => void }> = ({ task, onChanged }) => {
  const done = !!task.completed_at;
  const overdue = !done && new Date(task.due_date).getTime() < Date.now();
  const toggle = async () => {
    await fetch(`/api/crm/tasks/${task.id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ completed: !done })
    });
    onChanged();
  };
  const del = async () => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    await fetch(`/api/crm/tasks/${task.id}`, { method: 'DELETE', headers: authHeaders() });
    onChanged();
  };
  return (
    <li className={`flex items-center gap-2 text-xs rounded px-3 py-2 ${done ? 'bg-gray-50' : overdue ? 'bg-red-50' : 'bg-amber-50'}`}>
      <input type="checkbox" checked={done} onChange={toggle} className="cursor-pointer" />
      <div className="flex-1 min-w-0">
        <div className={`font-medium ${done ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{task.title}</div>
        <div className={`text-[10px] ${overdue ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
          Due {formatDateTime(task.due_date)}
          {overdue && ' · OVERDUE'}
        </div>
      </div>
      <button onClick={del} className="text-gray-300 hover:text-red-600 text-sm" title="Delete">×</button>
    </li>
  );
};

const Section: React.FC<{ title: string; right?: React.ReactNode; children: React.ReactNode }> = ({ title, right, children }) => (
  <div className="bg-white rounded-lg shadow p-5">
    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      {right}
    </div>
    {children}
  </div>
);

// ============================================================
// Add manual payment modal
// ============================================================

const AddPaymentModal: React.FC<{
  companyId: number;
  companyName: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ companyId, companyName, onClose, onSaved }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState(today);
  const [paymentMethod, setPaymentMethod] = useState('BACS');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setError(null);
    const num = parseFloat(amount);
    if (!Number.isFinite(num) || num <= 0) {
      setError('Enter a positive amount in pounds (e.g. 720.00)');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`/api/crm/companies/${companyId}/invoices`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          amountPence: Math.round(num * 100),
          paidAt: new Date(paidAt).toISOString(),
          description: description.trim() || null,
          invoiceNumber: invoiceNumber.trim() || null,
          paymentMethod: paymentMethod || null
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add');
      onSaved();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Log a manual payment</h2>
            <p className="text-xs text-gray-500 mt-0.5">For {companyName} — counts toward lifetime value alongside Stripe data.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl">×</button>
        </div>
        <div className="px-6 py-4 space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{error}</div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Amount (£)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              autoFocus
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="720.00"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Paid date</label>
              <input
                type="date"
                value={paidAt}
                onChange={e => setPaidAt(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Method</label>
              <select
                value={paymentMethod}
                onChange={e => setPaymentMethod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="BACS">BACS</option>
                <option value="Wire">Wire transfer</option>
                <option value="Cheque">Cheque</option>
                <option value="Cash">Cash</option>
                <option value="Xero invoice">Xero invoice (BACS)</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Invoice number (optional)</label>
            <input
              type="text"
              value={invoiceNumber}
              onChange={e => setInvoiceNumber(e.target.value)}
              placeholder="e.g. INV-0042"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description (optional)</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Mapping project for Q2…"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
        </div>
        <div className="px-6 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
          <button
            onClick={save}
            disabled={saving || !amount.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Add payment'}
          </button>
        </div>
      </div>
    </div>
  );
};
